import { Microfrontend } from '@cian/mf-react';
import { useBooleanState } from '@cian/react-utils';

import { type FC, type ReactNode, useCallback, useRef, useState } from 'react';

import { CallbackFallbackModal } from '../../components/CallbackFallbackModal';
import { type IOpenWidgetParams } from '../../types/microfrontends/newbuildingCallbackWidget';

import { NewbuildingCallbackWidgetContext } from './NewbuildingCallbackWidgetContext';
import { useNewbuildingCallbackWidget } from './hooks';
import { type TOpenWidgetResultHandler } from './types';

interface INewbuildingCallbackWidgetProviderProps {
  children: ReactNode;
}

/**
 * Компонент, обеспечивающий загрузку микрофронта модалок ОЗ на клиенте.
 *
 * 1. Пользователь кликает на кнопку, и вызывается `handleWidgetOpeningStart`.
 * 2. Если пользователь подходящий, то регистрируем манифест, загружаем ассеты
 * 3. Сеттим в React-стейт манифест. Ждем сеттинга с помощью useEffect.
 * 4. По стейту отрисовывается <Microfrontend />
 * 5. Инициализуруется реф на <Microfrontend />, содержащий локальное API.
 * 6. Вызываем на рефе локальное API, и открывается модалка.
 */
export const NewbuildingCallbackWidgetProvider: FC<INewbuildingCallbackWidgetProviderProps> = ({ children }) => {
  const paramsRef = useRef<IOpenWidgetParams | undefined>(undefined);
  const { state: isFallbackModalVisible, setTrue: showFallbackModal, setFalse: closeFallbackModal } = useBooleanState();
  const [isLoading, setLoading] = useState(false);

  const handleWidgetOpeningFinish = useCallback<TOpenWidgetResultHandler>(
    ({ isError }) => {
      if (isError) {
        if (!isFallbackModalVisible) {
          showFallbackModal();
        }
      } else if (isFallbackModalVisible && !isError) {
        closeFallbackModal();
      }
    },
    [closeFallbackModal, isFallbackModalVisible, showFallbackModal],
  );

  const { openWidget, widgetManifest, widgetRef } = useNewbuildingCallbackWidget(handleWidgetOpeningFinish);

  const handleWidgetOpeningStart = useCallback(
    async (params: IOpenWidgetParams) => {
      // Сохраняем в реф для работы с теми же параметрами на случай, если произойдет ошибка.
      paramsRef.current = params;

      await openWidget(params);
    },
    [openWidget],
  );

  const handleRetryClick = useCallback(async () => {
    setLoading(true);
    await openWidget(paramsRef.current as IOpenWidgetParams);
    setLoading(false);
  }, [openWidget]);

  return (
    <NewbuildingCallbackWidgetContext.Provider value={handleWidgetOpeningStart}>
      {children}
      {widgetManifest && (
        <Microfrontend
          image={widgetManifest.imageVersion}
          instance={widgetManifest.instanceId}
          name={widgetManifest.microfrontendName}
          ref={widgetRef}
          runtime={widgetManifest.runtimeName}
        />
      )}
      {isFallbackModalVisible && (
        <CallbackFallbackModal
          isLoading={isLoading}
          isOpen={isFallbackModalVisible}
          onClose={closeFallbackModal}
          onRetry={handleRetryClick}
        />
      )}
    </NewbuildingCallbackWidgetContext.Provider>
  );
};
